/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { useIsOnLocaleSubfolderRoute } from '@coursera-web/internationalization-lib';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button, Divider, Grid, Hidden, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import useIsBrowse from 'bundles/collections-common/utils/useIsBrowse';
import useIsMobile from 'bundles/common/hooks/useIsMobile';
import GrowthDiscovery from 'bundles/epic/clients/GrowthDiscovery';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import ActiveFilterItems from 'bundles/search-common/components/ActiveFilterItems';
import EndOfResults from 'bundles/search-common/components/EndOfResults';
import SearchPageBodySections from 'bundles/search-common/components/SearchPageBodySections';
import SearchResultsHeader from 'bundles/search-common/components/SearchResultsHeader';
import SearchResultsPagination from 'bundles/search-common/components/SearchResultsPagination';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import SuggestedSearches from 'bundles/search-common/components/SuggestedSearches';
import { CoachChatLayer } from 'bundles/search-common/components/experiments/coachInSearch/CoachChatLayer';
import { SearchCoachDisclosure } from 'bundles/search-common/components/experiments/coachInSearch/SearchCoachDisclosure';
import { getCoachSearchQueries } from 'bundles/search-common/components/experiments/coachInSearch/coachSearchQueries';
import AllSearchFiltersPanel from 'bundles/search-common/components/experiments/filters2024/AllSearchFiltersPanel';
import StickyFilterBar from 'bundles/search-common/components/experiments/filters2024/StickyFilterBar';
import ContextAwareFiltersButton from 'bundles/search-common/components/experiments/filters2024/components/ContextAwareFilterButton';
import FilterChipDropdowns from 'bundles/search-common/components/experiments/filters2024/components/FilterChipDropdowns';
import {
  NUMBER_OF_RESULTS_PER_PAGE,
  SEARCH_MAIN_INDEX_ID,
  SEARCH_RESULTS_BEFORE_UPSELL_CARD_DESKTOP,
  SEARCH_RESULTS_BEFORE_UPSELL_CARD_MOBILE,
  SEARCH_SUGGESTIONS_INDEX_ID,
} from 'bundles/search-common/constants';
import { PAGINATION_TYPE, type PaginationType } from 'bundles/search-common/constants/filter';
import { useOwnsCourseraPlus } from 'bundles/search-common/providers/LearnerProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import { getCombinedHits, getSearchUpsellHit, getUpsellCardPosition } from 'bundles/search-common/utils/SearchUtils';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import {
  isInBrowseFilterTestVariant,
  isInEQPFilterTestVariant,
  isInFilterTestVariant,
  showCoachInSearch,
} from 'bundles/search-common/utils/experimentUtils';
import { shouldShowFreeQueryPageConfigPreview } from 'bundles/search-common/utils/pageConfigUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import {
  isSearchSuggestionHit,
  mapSuggestionHitToHighlightItem,
  shouldShowSuggestedSearches,
} from 'bundles/search-common/utils/utils';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/search-common';

export const styles = {
  headerBase: css`
    display: flex;
    justify-content: space-between;

    /* breakpoints.md */
    @media (max-width: 1023px) {
      min-height: var(--cds-spacing-400);
    }

    @media (min-width: 600px) {
      padding-left: var(--cds-spacing-100);
      padding-right: var(--cds-spacing-100);
    }
  `,
  headerWithMargin: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  headerMobile: css`
    margin-bottom: 0;
  `,
  mobileSortByLayoutShim: css`
    margin-top: var(--cds-spacing-50);
  `,
  fullHeight: css`
    height: 100%;
  `,
  desktopSearchControlsWrapper: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
  filterTestResultItems: css`
    flex-shrink: 1;
  `,
  filterTestSortBy: css`
    flex-shrink: 0;

    & > div {
      margin-left: auto;
    }
  `,
  stickyFilterHeaderWrap: css`
    /* needed for sticky logic */
    min-height: 70px;
    position: relative;
    width: 100%;
  `,
};

export type EventingData = {
  searchIndex: string;
  searchRecommender?: Recommender;
  searchIndexPosition: number;
  page: string;
  searchQuery: string;
  filtersApplied: Record<string, string[]>;
};

type Props = {
  optimisticActiveFilterCount: number;
  isSEOEntityPageWithQuery: boolean;
  eventingData: EventingData;
  paginationType: PaginationType;
  searchQuery?: string | undefined;
  page: number;
  showCplusUpsell?: boolean;
  showDesktopSortBy?: boolean;
  showRelatedQueries?: boolean;
  enableScrollToResultsOnPageChange?: boolean;
  enableFilters?: boolean;
  hideHitsCount?: boolean;
  showResultsHeader?: boolean;
  filterStatusTags?: (tags: string[]) => string[];
  facets?: string[];
  shouldShowAllResultsText?: boolean;
};

const SearchPageBody = ({
  optimisticActiveFilterCount,
  isSEOEntityPageWithQuery,
  eventingData,
  paginationType,
  searchQuery,
  page,
  showCplusUpsell = false,
  showDesktopSortBy = false,
  showRelatedQueries = false,
  shouldShowAllResultsText = false,
  enableScrollToResultsOnPageChange = false,
  enableFilters = false,
  hideHitsCount = true,
  showResultsHeader = true,
  filterStatusTags = (t: string[]) => t,
  facets,
}: Props) => {
  const userAgent = useUserAgent();
  const isMobile = useMediaQuery(breakpoints.down('sm'), {
    defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
  });
  const isConsumerSearchPage = useIsConsumerSerp();
  const isOnLocaleSubfolderRoute = useIsOnLocaleSubfolderRoute();
  const { getSearchResults, setPage, removeFilters, clearFilters, setSortBy, loadNextPage, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const {
    query,
    pagination,
    totalPages,
    facetFilters,
    hitsDisplayedCount = 0,
    hitsTotalCount = 0,
    inferredCurrentPageNumber = 0,
    sortBy,
    elements,
    page: currentPage,
    limit,
    autoAppliedFilters,
  } = searchData || {};
  const resultsPerPage = limit ?? NUMBER_OF_RESULTS_PER_PAGE;
  const totalHits = pagination?.totalElements;
  const suggestedSearches = getSearchResults(SEARCH_SUGGESTIONS_INDEX_ID);
  const activeFilters = useMemo(
    () => [...new Set(facetFilters?.flat()), ...(autoAppliedFilters?.flat() || [])],
    [facetFilters, autoAppliedFilters]
  );
  const [showAllFiltersModal, setShowAllFiltersModal] = React.useState(false);
  const [initialExpandedGroup, setInitialExpandedGroup] = React.useState<string | undefined>();
  const isOnLastPage =
    !isLoading && (inferredCurrentPageNumber >= (totalPages ?? 0) || hitsDisplayedCount === hitsTotalCount);
  const loadNextSearchPage = () => {
    if (!isOnLastPage) {
      loadNextPage();
    }
  };
  const isInfiniteStylePagination = paginationType === PAGINATION_TYPE.INFINITE;
  const isPaginatedStylePagination = paginationType === PAGINATION_TYPE.PAGINATED;
  const isOnEQPSubfolderRoute = isOnLocaleSubfolderRoute && isSEOEntityPageWithQuery;

  const isBrowsePage = useIsBrowse();
  const isEqpPage = useIsEqp();
  const inFilterTestVariantAndOnSearch =
    (isEqpPage && isInEQPFilterTestVariant()) ||
    (isBrowsePage && isInBrowseFilterTestVariant()) ||
    (isConsumerSearchPage && isInFilterTestVariant());
  const track = useTracker();
  const suggestedSearchHits = useMemo(
    () => suggestedSearches?.elements.filter(isSearchSuggestionHit),
    [suggestedSearches]
  );

  const isEligibleForCoachInSearch: boolean =
    !isSEOEntityPageWithQuery &&
    isAuthenticatedUser() &&
    Boolean(getCoachSearchQueries()[query || '']) &&
    !shouldShowFreeQueryPageConfigPreview(query);

  const { setCoachOpen } = useCoachState();

  useEffect(() => {
    setCoachOpen(false);
    // query in dep list because we want to close coach if the query changes
  }, [setCoachOpen, query]);

  const isMobileView = useIsMobile();
  const resultsBeforeUpsellCard = isMobileView
    ? SEARCH_RESULTS_BEFORE_UPSELL_CARD_MOBILE
    : SEARCH_RESULTS_BEFORE_UPSELL_CARD_DESKTOP;
  const ownsCourseraPlus = useOwnsCourseraPlus();
  let showMerchandisingPlacements = false;
  if (!ownsCourseraPlus && (isConsumerSearchPage || isEqpPage)) {
    showMerchandisingPlacements = GrowthDiscovery.get('enableCardTakeoverExperiment');
  }
  const upsellCardIndex = page === 1 ? getUpsellCardPosition(isMobileView, showMerchandisingPlacements) : -1;
  const isSeoEntityPagePath = useIsEqp();

  const showUpsellCard =
    (showCplusUpsell || showMerchandisingPlacements) && page === 1 && isSeoEntityPagePath && upsellCardIndex !== -1;
  const addOverFlowCard = !showUpsellCard && showMerchandisingPlacements;

  let infiniteSearchElements = [];
  if (showMerchandisingPlacements) {
    infiniteSearchElements = [
      ...(searchData?.elements.slice(0, resultsBeforeUpsellCard) || []),
      getSearchUpsellHit(),
      ...(searchData?.elements.slice(resultsBeforeUpsellCard) || []),
    ];
  } else {
    infiniteSearchElements = searchData?.elements || [];
  }

  const urlParams = useLocation().search;
  const searchParams = useMemo(() => {
    const urlParamsObject = new URLSearchParams(urlParams);
    urlParamsObject.delete('page');
    const params: Record<string, string> = {};
    urlParamsObject.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [urlParams]);

  const combinedHits = isLoading
    ? []
    : getCombinedHits({
        hits: elements,
        page,
        totalHits,
        showUpsellCard,
        upsellCardIndex,
        searchParams,
        addOverFlowCard,
      });

  const shouldShowRelatedQueries =
    showRelatedQueries && !isOnEQPSubfolderRoute && shouldShowSuggestedSearches(query, suggestedSearches?.elements);

  const shouldShowRelatedQueriesInfinite =
    shouldShowRelatedQueries &&
    !(showCoachInSearch() && isEligibleForCoachInSearch) &&
    (suggestedSearches?.elements.length ?? 0) > 0;

  return (
    <CoachChatLayer searchHits={searchData?.elements ?? []} isEligibleForCoachInSearch={isEligibleForCoachInSearch}>
      {inFilterTestVariantAndOnSearch && (
        <AllSearchFiltersPanel
          isOpen={showAllFiltersModal}
          onClose={() => {
            setShowAllFiltersModal(false);
            setInitialExpandedGroup(undefined);
            track('close_modal', {
              modal: {
                name: 'all_search_filters',
              },
            });
          }}
          eventingData={eventingData}
          initialExpandedGroup={initialExpandedGroup}
          facets={facets}
        />
      )}
      <>
        <Grid
          container
          justifyContent="space-between"
          css={[
            styles.headerBase,
            !inFilterTestVariantAndOnSearch && styles.headerWithMargin,
            isMobile && styles.headerMobile,
          ]}
        >
          <Grid
            item
            {...(inFilterTestVariantAndOnSearch && { xs: 6, sm: 8 })}
            css={[styles.mobileSortByLayoutShim, inFilterTestVariantAndOnSearch && styles.filterTestResultItems]}
            id="search-results-header-wrapper"
          >
            <div aria-live="polite" aria-atomic="true">
              {showResultsHeader && (
                <SearchResultsHeader
                  searchQuery={inferQueryFromParams(searchQuery || '')}
                  numberOfHits={totalHits || undefined}
                  shouldHide={isMobile && !inFilterTestVariantAndOnSearch}
                  activeFilterCount={optimisticActiveFilterCount}
                  isLoading={isLoading && hitsDisplayedCount === 0}
                  lessenHeaderFocus={true}
                  hideHits={hideHitsCount}
                  shouldShowAllResultsText={shouldShowAllResultsText}
                />
              )}
            </div>
          </Grid>
          <Grid
            item
            {...(inFilterTestVariantAndOnSearch && { xs: 6, sm: 4 })}
            css={[styles.desktopSearchControlsWrapper, inFilterTestVariantAndOnSearch && styles.filterTestSortBy]}
          >
            {/* desktop sortby */}
            {showDesktopSortBy && (!isMobile || inFilterTestVariantAndOnSearch) && (
              <SortByIndex
                setSortBy={(sortByValue) => setSortBy(SEARCH_MAIN_INDEX_ID, sortByValue)}
                sortByValue={sortBy}
                hideLabelOnMobile={inFilterTestVariantAndOnSearch}
              />
            )}
          </Grid>
        </Grid>
        {enableFilters && inFilterTestVariantAndOnSearch && (
          <div css={styles.stickyFilterHeaderWrap}>
            <StickyFilterBar
              shouldAffixFilterOnMobile={false}
              filterComponents={[
                <ContextAwareFiltersButton
                  key="filter-button"
                  onClick={() => {
                    track('view_modal', {
                      modal: {
                        name: 'all_search_filters',
                      },
                    });
                    setShowAllFiltersModal(true);
                  }}
                />,
                <Divider
                  key="divider"
                  orientation="vertical"
                  css={css`
                    width: 1px !important;
                  `}
                />,
                <FilterChipDropdowns
                  key="filter-chips"
                  eventingData={eventingData}
                  onOpenAllFiltersPanel={(filterGroupKey) => {
                    setInitialExpandedGroup(filterGroupKey);
                    setShowAllFiltersModal(true);
                  }}
                  facets={facets}
                />,
                ...(activeFilters && activeFilters.length > 0
                  ? [
                      <Button
                        key="clear-button"
                        aria-label={_t('Clear all filters')}
                        variant="ghost"
                        onClick={() => {
                          clearFilters(SEARCH_MAIN_INDEX_ID);
                          if (isMobile) {
                            const scrollableContainer = document.querySelector('.sticky-filter-bar');
                            if (scrollableContainer) {
                              scrollableContainer.scrollTo({ left: 0 });
                            }
                          }
                        }}
                        css={css`
                          color: var(--cds-color-grey-975);
                          min-width: var(--cds-spacing-1000);
                        `}
                        size="small"
                      >
                        {_t('Clear all')}
                      </Button>,
                    ]
                  : []),
              ]}
            />
          </div>
        )}
        {!isMobile && !inFilterTestVariantAndOnSearch && activeFilters && (
          <Hidden smDown>
            <ActiveFilterItems
              items={activeFilters}
              removeFilter={(filter: string) => removeFilters(SEARCH_MAIN_INDEX_ID, [filter])}
              clearFilters={() => clearFilters(SEARCH_MAIN_INDEX_ID)}
              eventingData={eventingData}
              isLoading={isLoading}
            />
          </Hidden>
        )}
        <div role="region" aria-label={_t('Search Results')} id="searchResults">
          {isPaginatedStylePagination && (
            <SearchPageBodySections
              hits={combinedHits || []}
              resultsPerPage={resultsPerPage}
              forceOpenInNewTab
              eventingData={eventingData}
              isLoading={isLoading}
              isSearchPage={isConsumerSearchPage}
              isSeoEntityPagePath={isSeoEntityPagePath}
              page={page}
              filterStatusTags={filterStatusTags}
            />
          )}

          {!isInfiniteStylePagination && shouldShowRelatedQueries && (
            <div>
              <SuggestedSearches
                query={query}
                hits={mapSuggestionHitToHighlightItem(suggestedSearchHits, query)}
                eventingData={eventingData}
              />
            </div>
          )}

          {isInfiniteStylePagination && (
            <>
              {/* first search cards section  */}
              <SearchPageBodySections
                hits={infiniteSearchElements.slice(0, resultsPerPage)}
                resultsPerPage={resultsPerPage}
                forceOpenInNewTab
                eventingData={eventingData}
                isLoading={isLoading && !hitsDisplayedCount}
                isSearchPage={isConsumerSearchPage}
                onScrolledToBottom={loadNextSearchPage}
                totalHitsDisplayed={hitsDisplayedCount}
                isFirstSection={true}
              />
              {isOnLastPage && hitsDisplayedCount < resultsPerPage && (
                <EndOfResults hitsDisplayedCount={hitsDisplayedCount} hitsTotalCount={hitsTotalCount} />
              )}

              {/* Coach in Search experiment */}
              {showCoachInSearch() && isEligibleForCoachInSearch && (
                <div css={{ marginTop: '24px', marginBottom: '50px' }}>
                  <SearchCoachDisclosure query={query || ''} setCoachOpen={setCoachOpen} />
                </div>
              )}

              {/* suggestions  */}
              {shouldShowRelatedQueriesInfinite && (
                <SuggestedSearches
                  query={query}
                  hits={mapSuggestionHitToHighlightItem(suggestedSearchHits, query)}
                  eventingData={eventingData}
                  css={{ marginTop: '31px', marginBottom: '97px' }}
                />
              )}

              {/* second search cards section */}
              {hitsDisplayedCount >= resultsPerPage && (
                <SearchPageBodySections
                  hits={infiniteSearchElements.slice(resultsPerPage)}
                  resultsPerPage={resultsPerPage}
                  forceOpenInNewTab
                  eventingData={eventingData}
                  isLoading={isLoading}
                  isSearchPage={isConsumerSearchPage}
                  onScrolledToBottom={loadNextSearchPage}
                  totalHitsDisplayed={hitsDisplayedCount}
                  cardIndexOffset={resultsPerPage}
                  isFirstSection={false}
                />
              )}

              {isOnLastPage && hitsDisplayedCount > resultsPerPage && (
                <EndOfResults hitsDisplayedCount={hitsDisplayedCount} hitsTotalCount={hitsTotalCount} />
              )}
            </>
          )}

          {!isInfiniteStylePagination && (
            <SearchResultsPagination
              shouldRenderLinks={isSEOEntityPageWithQuery}
              activeFilters={activeFilters}
              paginationData={{ ...pagination, totalPages, page: currentPage || page }}
              currentQuery={query}
              eventingData={eventingData}
              setPage={(pageNum: number) => setPage(SEARCH_MAIN_INDEX_ID, pageNum)}
              enableScrollToResultsOnPageChange={enableScrollToResultsOnPageChange}
              resultsPerPage={resultsPerPage}
            />
          )}
        </div>
      </>
    </CoachChatLayer>
  );
};

export default SearchPageBody;
